import React from 'react';
/**
 * LineChartRenderer — Interactive line chart with dot selection.
 *
 * DDD co-locatie: moved from components/practice/renderers/ in s67.
 * Optimized: extracted shared data normalization + selection hook (~272r → ~155r).
 */
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { QuestionRendererProps } from './types';
import { normalizeChartData, ChartDataPoint } from './chartDataUtils';
import { useChartSelection } from './useChartSelection';
import { FALLBACK_COLOR, hexToRgba, CHART_OPTION_BUTTON_CLASS } from './rendererUtils';
export function LineChartRenderer({
  question,
  onAnswer,
  onReadyChange,
  shouldValidate,
  moduleColor
}: QuestionRendererProps) {
  // Sprint R-CLEANUP (F10 follow-up): derive accent from moduleColor, allow
  // seed override via config.lineColor. Closes the App-Feel §3.1 blocker
  // for line-chart visuals in non-indigo modules.
  const color = moduleColor || FALLBACK_COLOR;
  const config = question.data || {};
  const data = normalizeChartData(config);
  const xAxisLabel = config.xAxisLabel || '';
  const yAxisLabel = config.yAxisLabel || '';
  const lineColor = config.lineColor || color;
  const selectionRingColor = hexToRgba(color, 0.55);
  const hideValues = config.hideValues !== false;
  const {
    selectedIndex,
    handleItemClick,
    handleKeyDown
  } = useChartSelection({
    questionId: question.id,
    itemCount: data.length,
    idPrefix: 'line-option',
    onAnswer,
    onReadyChange,
    shouldValidate
  });
  const CustomDot = (props: {
    cx?: number;
    cy?: number;
    index?: number;
  }) => {
    const {
      cx,
      cy,
      index
    } = props;
    if (cx === undefined || cy === undefined) return null;
    const isSelected = selectedIndex === index;
    return <g data-id="element-10711">
        {/* STU UX Audit P0: hit-zone r=20 → r=24 (48×48px) so the
                invisible touch-target clears Fitts' Law on mobile. */}
        <circle cx={cx} cy={cy} r={24} fill="transparent" cursor="pointer" pointerEvents="all" onClick={() => handleItemClick(null, index!)} data-id="element-10712" />
        <circle cx={cx} cy={cy} r={isSelected ? 10 : 6} fill={isSelected ? lineColor : '#fff'} stroke={lineColor} strokeWidth={isSelected ? 3 : 2} cursor="pointer" onClick={() => handleItemClick(null, index!)} data-id="element-10713" />
        {isSelected && <circle cx={cx} cy={cy} r={16} fill="none" stroke={selectionRingColor} strokeWidth={2} strokeDasharray="4 2" opacity={0.7} data-id="element-10714" />}
      </g>;
  };
  const CustomTooltip = ({
    active,
    payload
  }: {
    active?: boolean;
    payload?: Array<{
      payload: {
        label: string;
        value: number;
      };
    }>;
  }) => {
    if (!active || !payload?.[0]) return null;
    const item = payload[0].payload;
    return <div className="bg-white px-3 py-2 rounded-lg shadow-lg border border-slate-200 text-sm" data-id="element-10715">
        <span className="font-semibold text-slate-700" data-id="element-10716">{item.label}</span>
        {!hideValues && <span className="text-slate-500 ml-2" data-id="element-10717">{item.value}</span>}
      </div>;
  };
  return <div className="w-full max-w-3xl mx-auto flex flex-col items-center" data-id="element-10718">
      <div className="w-full h-[300px] md:h-[400px] bg-white rounded-xl border border-slate-200 shadow-sm p-4" aria-hidden="true" data-id="element-10719">
        <ResponsiveContainer width="100%" height="100%" data-id="element-10720">
          <LineChart data={data} margin={{
          top: 20,
          right: 30,
          left: 20,
          bottom: 40
        }} data-id="element-10721">
            <CartesianGrid strokeDasharray="3 3" vertical={false} data-id="element-10722" />
            <XAxis dataKey="label" tick={{
            fill: '#64748b',
            fontSize: 12
          }} tickLine={false} axisLine={{
            stroke: '#cbd5e1'
          }} label={xAxisLabel ? {
            value: xAxisLabel,
            position: 'insideBottom',
            offset: -20,
            fill: '#475569',
            fontSize: 14,
            fontWeight: 600
          } : undefined} data-id="element-10723" />
            <YAxis tick={{
            fill: '#64748b',
            fontSize: 12
          }} tickLine={false} axisLine={false} label={yAxisLabel ? {
            value: yAxisLabel,
            angle: -90,
            position: 'insideLeft',
            fill: '#475569',
            fontSize: 14,
            fontWeight: 600
          } : undefined} data-id="element-10724" />
            <Tooltip content={<CustomTooltip data-id="element-10726" />} cursor={{
            stroke: '#94a3b8',
            strokeWidth: 1,
            strokeDasharray: '4 4'
          }} data-id="element-10725" />
            <Line type="monotone" dataKey="value" stroke={lineColor} strokeWidth={3} dot={<CustomDot data-id="element-10728" />} activeDot={false} data-id="element-10727" />
          </LineChart>
        </ResponsiveContainer>
      </div>

      <div role="listbox" aria-label="Kies een punt" className="flex flex-wrap justify-center gap-2 mt-4" data-id="element-10729">
        {data.map((entry: ChartDataPoint, index: number) => {
        const isSelected = selectedIndex === index;
        const baseClass = CHART_OPTION_BUTTON_CLASS;
        const unselectedClass = 'bg-slate-100 text-slate-700 hover:bg-slate-200 focus:ring-slate-400';
        return <button key={`option-${index}`} id={`line-option-${index}`} role="option" aria-selected={isSelected} tabIndex={0} onClick={() => handleItemClick(null, index)} onKeyDown={e => handleKeyDown(e, index)} style={isSelected ? {
          backgroundColor: color,
          color: '#ffffff'
        } : undefined} className={isSelected ? `${baseClass} focus:ring-current` : `${baseClass} ${unselectedClass}`} data-id="element-10730">
              {entry.label}
            </button>;
      })}
      </div>

      <p className="text-center text-slate-500 text-sm mt-4" data-id="element-10731">
        Klik op een punt of gebruik de pijltjestoetsen
      </p>
    </div>;
}